import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthSession } from '@/features/auth/useAuthSession';
import {
  announceCollection as announceCollectionRequest,
  fetchAdminAnalytics,
  fetchAdminCollections,
  fetchAdminFailures,
  fetchAdminOverview,
  fetchAdminSettings,
  saveAdminSetting,
  setCollectionVisibility,
} from '@/services/admin';

/**
 * Данные и действия админки.
 *
 * Один хук на весь экран: разделы читаются вместе и обновляются вместе,
 * а держать пять отдельных подписок ради этого незачем. Ни одно значение
 * здесь не считается на клиенте — всё приходит из Edge Function, которая
 * проверяет права при каждом запросе.
 */
export function useAdminConsole() {
  const { status, session } = useAuthSession();
  const queryClient = useQueryClient();

  const token = session?.access_token ?? '';
  const enabled = status === 'ready' && Boolean(session);

  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => fetchAdminOverview(token),
    enabled,
    // Отказ в правах не станет успехом от повтора.
    retry: false,
  });

  const settings = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => fetchAdminSettings(token),
    enabled,
    retry: false,
  });

  const failures = useQuery({
    queryKey: ['admin', 'failures'],
    queryFn: () => fetchAdminFailures(token),
    enabled,
    retry: false,
  });

  const analytics = useQuery({
    queryKey: ['admin', 'analytics'],
    queryFn: () => fetchAdminAnalytics(token),
    enabled,
    retry: false,
  });

  const collections = useQuery({
    queryKey: ['admin', 'collections'],
    queryFn: () => fetchAdminCollections(token),
    enabled,
    retry: false,
  });

  const saveSetting = useMutation({
    mutationFn: (input: { key: string; value: number }) => saveAdminSetting(input, token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });

  const toggleCollection = useMutation({
    mutationFn: (input: { slug: string; enabled: boolean }) =>
      setCollectionVisibility(input, token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'collections'] });
      // Витрина у всех тоже изменилась.
      void queryClient.invalidateQueries({ queryKey: ['collections'] });
    },
  });

  const announceCollection = useMutation({
    mutationFn: (input: { slug: string }) => announceCollectionRequest(input, token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'collections'] });
    },
  });

  return {
    overview,
    settings,
    failures,
    analytics,
    collections,
    saveSetting,
    toggleCollection,
    announceCollection,
  };
}
